import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Kicker } from "@/components/Reveal";
import { BALEAL_LOGO_URL } from "@/data/content";

const Testimonials = () => {
  const { t } = useLanguage();
  const tm = t.testimonials;
  const [i, setI] = useState(0);
  const item = tm.items[i];

  const go = (dir) => {
    setI((prev) => (prev + dir + tm.items.length) % tm.items.length);
  };

  return (
    <section className="bg-cream-2 py-24 md:py-36" data-testid="testimonials">
      <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
        <Reveal>
          <Kicker>{tm.kicker}</Kicker>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-serif font-light text-4xl sm:text-5xl text-charcoal mt-5 mb-12">
            {tm.title}
          </h2>
        </Reveal>

        <div className="relative min-h-[260px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              data-testid={`testimonial-${i}`}
            >
              <p className="font-serif font-light text-2xl md:text-3xl text-charcoal leading-[1.4] max-w-3xl mx-auto">
                “{item.quote}”
              </p>
              <footer className="mt-8">
                <p className="font-mono text-[0.7rem] uppercase tracking-[0.22em] text-olive">
                  {item.author}
                </p>
                <p className="text-sm text-taupe mt-1">{item.role}</p>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-center gap-6 mt-10">
          <button
            onClick={() => go(-1)}
            data-testid="testimonial-prev"
            className="w-11 h-11 flex items-center justify-center border border-border-subtle text-charcoal hover:bg-olive hover:text-cream hover:border-olive transition-colors"
            aria-label="Previous"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="flex gap-2">
            {tm.items.map((_, di) => (
              <span
                key={di}
                className={`h-1.5 rounded-full transition-all duration-300 ${di === i ? "w-6 bg-olive" : "w-1.5 bg-taupe/50"}`}
              />
            ))}
          </div>
          <button
            onClick={() => go(1)}
            data-testid="testimonial-next"
            className="w-11 h-11 flex items-center justify-center border border-border-subtle text-charcoal hover:bg-olive hover:text-cream hover:border-olive transition-colors"
            aria-label="Next"
          >
            <ArrowRight size={16} />
          </button>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16 pt-12 border-t border-border-subtle flex flex-col items-center gap-5">
            <p className="font-mono text-[0.7rem] uppercase tracking-[0.22em] text-taupe">
              {t.clients.kicker}
            </p>
            <img
              src={BALEAL_LOGO_URL}
              alt="Baleal Wellness"
              className="h-20 md:h-24 w-auto object-contain opacity-90"
              data-testid="client-baleal-logo"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Testimonials;
